/**
 * Auto-Fill Field Suggestion Component
 * Displays auto-fill suggestions for individual fields right below the field
 */

import React, { useState, useCallback } from 'react';
import './AutoFillFieldSuggestion.css';

const AutoFillFieldSuggestion = ({
    fieldName,
    suggestions = {},
    isLoading = false,
    showSuggestions = false,
    onApplyField,
    onIgnoreField,
    className = ''
}) => {
    const [appliedFields, setAppliedFields] = useState(new Set());
    const [ignoredFields, setIgnoredFields] = useState(new Set());

    // Show all suggestions when available
    const fieldSuggestions = suggestions;
    const hasSuggestions = Object.keys(fieldSuggestions).length > 0;

    const handleApplyField = useCallback((field, value) => {
        setAppliedFields(prev => new Set([...prev, field]));
        onApplyField?.(field, value);
    }, [onApplyField]);

    const handleIgnoreField = useCallback((field) => {
        setIgnoredFields(prev => new Set([...prev, field]));
        onIgnoreField?.(field);
    }, [onIgnoreField]);

    // Don't render if no suggestions or not showing
    if (!showSuggestions || !hasSuggestions) {
        return null;
    }

    const getFieldIcon = (fieldName) => {
        const field = fieldName.toLowerCase();
        if (field.includes('email')) return '📧';
        if (field.includes('phone') || field.includes('telephone')) return '📞';
        if (field.includes('name') || field.includes('person')) return '👤';
        if (field.includes('address') || field.includes('street')) return '📍';
        if (field.includes('city') || field.includes('state')) return '🏙️';
        if (field.includes('organization') || field.includes('company')) return '🏢';
        if (field.includes('website') || field.includes('url')) return '🌐';
        if (field.includes('amount') || field.includes('price')) return '💰';
        if (field.includes('date')) return '📅';
        if (field.includes('description') || field.includes('notes')) return '📝';
        return '📋';
    };

    const getFieldType = (field) => {
        const fieldLower = field.toLowerCase();
        if (fieldLower.includes('email') || fieldLower.includes('mail')) return 'email';
        if (fieldLower.includes('phone') || fieldLower.includes('tel') || fieldLower.includes('mobile')) return 'phone';
        if (fieldLower.includes('name') || fieldLower.includes('first') || fieldLower.includes('last') || fieldLower.includes('contact')) return 'name';
        if (fieldLower.includes('address') || fieldLower.includes('street') || fieldLower.includes('city') || fieldLower.includes('state') || fieldLower.includes('zip')) return 'address';
        return 'generic';
    };

    const getConfidenceLevel = (field, value) => {
        if (!value || typeof value !== 'string') {
            return 'low';
        }

        // Field-specific confidence scoring
        const fieldType = getFieldType(field);
        switch (fieldType) {
            case 'email':
                return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? 'high' : 'low';
            case 'phone':
                return /^[\d\s\-\+\(\)]{8,}$/.test(value) ? 'high' : 'medium';
            case 'name':
                return value.length > 2 && /^[A-Za-z\s]+$/.test(value) ? 'high' : 'medium';
            case 'address':
                return value.length > 10 ? 'high' : 'medium';
            default:
                // Generic scoring for other fields
                let score = 0;
                if (value.length > 5) score += 20;
                if (value.length > 20) score += 25;

                if (score >= 35) return 'high';
                if (score >= 20) return 'medium';
                return 'low';
        }
    };

    const formatFieldName = (fieldName) => {
        return fieldName
            .replace(/_/g, ' ')
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    };

    return (
        <div className={`auto-fill-field-suggestion ${className}`}>
            {isLoading ? (
                <div className="field-suggestion-loading">
                    <div className="field-spinner"></div>
                    <span>Finding suggestions...</span>
                </div>
            ) : (
                <div className="field-suggestion-content">
                    <div className="field-suggestion-header">
                        <div className="field-suggestion-title">
                            <span className="field-icon">{getFieldIcon(fieldName)}</span>
                            <span>Auto-fill suggestions</span>
                            <span className="suggestion-count">({Object.keys(fieldSuggestions).length})</span>
                        </div>
                    </div>

                    <div className="field-suggestion-items">
                        {Object.entries(fieldSuggestions).map(([field, value]) => {
                            const isApplied = appliedFields.has(field);
                            const isIgnored = ignoredFields.has(field);
                            const confidence = getConfidenceLevel(field, value);

                            return (
                                <div
                                    key={field}
                                    className={`field-suggestion-item ${isApplied ? 'applied' : ''} ${isIgnored ? 'ignored' : ''}`}
                                >
                                    <div className="field-suggestion-main">
                                        <div className="field-suggestion-label">
                                            <span className="field-icon-small">{getFieldIcon(field)}</span>
                                            <span className="field-name">{formatFieldName(field)}</span>
                                        </div>
                                        <div className="field-suggestion-value">
                                            {value}
                                        </div>
                                    </div>
                                    <div className="field-suggestion-meta">
                                        <div className={`field-confidence-badge confidence-${confidence}`}>
                                            {confidence === 'high' ? 'High' : confidence === 'medium' ? 'Med' : 'Low'}
                                        </div>
                                        <div className="field-suggestion-actions">
                                            <button
                                                type="button"
                                                className="field-btn-apply"
                                                onClick={() => handleApplyField(field, value)}
                                                disabled={isApplied}
                                                title={`Apply ${formatFieldName(field)}`}
                                            >
                                                {isApplied ? '✓ Applied' : 'Apply'}
                                            </button>
                                            <button
                                                type="button"
                                                className="field-btn-ignore"
                                                onClick={() => handleIgnoreField(field)}
                                                disabled={isIgnored}
                                                title={`Ignore ${formatFieldName(field)}`}
                                            >
                                                {isIgnored ? '✕ Ignored' : 'Ignore'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AutoFillFieldSuggestion;
